/**
 * The PILOT sheet's **EDIT mode** (`decisions/0206` point 4): the controls {@link PilotBuildSheet}
 * mounts in place when its host passes a {@link PilotSheetEdit}. In the header: the callsign and
 * name inputs and the portrait upload. In the card column: LICENSE LEVEL with GRIT, the HASE
 * steppers, and BONDMATE once Bonded is held. In the loadout column: steppers and ✕ on the skill
 * triggers, talents and licenses, ✕ on core bonuses and pilot gear, the damage-type pick on a Set
 * Damage Type weapon (the Signatures, `set_pilot_gear_damage_type`), a ＋ ADD under each section,
 * and the background's origin picker. Over all of it sit the red `EDITING · AUTO-SAVED · UNDO ·
 * REDO · DONE` bar and the build line.
 *
 * Every control sends a roster command, gated by the same budget
 * helpers the reducer uses (`haseBudget`, `skillBudget`, `talentBudget`, `licenseBudget`,
 * `rankCeilingAtLevel`, `coreBonusAffordance`, `pilotGearAffordance`), and the reducer stays the
 * wall: a rejection shows in the sheet's error line.
 *
 * **The build line.** `buildSignals` (the advisor FORGE reads too) flags each
 * section with outstanding work on its head, the reasons as the head's title, and the line under
 * the EDITING bar counts them (`n UNSPENT`, `n INVALID`, or `BUILD COMPLETE`). Each section head
 * also carries its budget as a meter. The flag is painted, not laid out, so a section finishing
 * mid-edit moves nothing.
 *
 * **The level-up diff.** EDIT captures the pilot when it turns on; a HASE point, trigger, talent or
 * license rank gained since then wears an accent outline until EDIT turns off.
 */
import { faPlus, faTriangleExclamation, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState, type ReactNode } from "react";
import {
  CHOSEN_DAMAGE_TYPES,
  GMS_LICENSE_ID,
  haseBudget,
  licenseBudget,
  licenseRankOf,
  MAX_LICENSE_RANK,
  MAX_TRIGGER_RANK,
  rankCeilingAtLevel,
  rankSum,
  skillBudget,
  spentLicenseRanks,
  talentBudget,
  triggerBonus,
  type PilotData,
  type PilotGearCategory,
  type RosterCommand,
} from "@lancer-vtt/domain";
import type { ComputedPilot } from "@lancer-vtt/rules";
import type { Compendium } from "@lancer-vtt/compendium";
import { CompendiumExplorer, coreBonusAffordance, decorationFor, filterUnowned, licenseSlug, pilotGearAffordance, pilotGearCategoryLabel, snapshotBaseline, sortByName } from "../../lib/editor";
import { buildSignals, type BuildSection, type BuildSignal } from "../../lib/editor/buildAdvisor";
import { Icon } from "../../lib/Icon";
import { RulesText } from "../../lib/RulesText";
import { uploadPilotImage } from "../../lib/api";
import { useDraftText } from "../../lib/useDraftText";
import type { LoadoutGroup, LoadoutRow, SectionFlag } from "../../lib/encounter/unitLoadout";
import { CompendiumSource, pilotGearProfile, type PilotGear, type Ref } from "../compendium/compendiumData";
import { CompendiumRecordReader } from "../compendium/CompendiumDetail";
import { Stepper } from "./brutalistRows";
import { SECTION_ANCHOR } from "./forgeGuide";
import { EditBar, EditToggle, ImageUpload, imageRejection, sectionFlag, type SheetEdit } from "./sheetEdit";

/** The pilot-weapon tag whose damage type the player picks (the Signatures, Core p.113). */
const SET_DAMAGE_TYPE_TAG = "tg_set_damage_type";

/** The PILOT sheet's editing capability: undo over `pilotScope`, and the roster's other pilots for BONDMATE. */
export type PilotSheetEdit = SheetEdit & { otherPilots: readonly { id: string; callsign: string }[] };

const ROMAN = ["", "I", "II", "III", "IV", "V", "VI"];
const HASE: { key: "hull" | "agi" | "sys" | "eng"; label: string; abbr: string }[] = [
  { key: "hull", label: "Hull", abbr: "HULL" },
  { key: "agi", label: "Agility", abbr: "AGILITY" },
  { key: "sys", label: "Systems", abbr: "SYSTEMS" },
  { key: "eng", label: "Engineering", abbr: "ENGINEERING" },
];
/** The build section each loadout group's head flags and anchors. Pilot gear's one flag and anchor ride its first group. */
const GROUP_SECTION: Record<string, Exclude<BuildSection, "Loadout">> = {
  triggers: "Skill Triggers",
  talents: "Talents",
  licenses: "Licenses",
  "core-bonuses": "Core Bonuses",
  weapons: "Pilot Gear",
};
const GEAR_GROUP: Record<PilotGearCategory, { id: string; title: string }> = {
  weapon: { id: "weapons", title: "WEAPONS" },
  armor: { id: "armor", title: "ARMOR" },
  gear: { id: "gear", title: "GEAR" },
};

type Explorer = "background" | "skill" | "talent" | "license" | "core_bonus" | `gear:${PilotGearCategory}`;

/** What EDIT mode adds to the PILOT sheet, slot by slot; every slot is `undefined` while EDIT is off. */
export type PilotSheetEditSlots = {
  toggle?: ReactNode;
  nameEditor?: ReactNode;
  headerControls?: ReactNode;
  toolbar?: ReactNode;
  error?: string | null;
  /** LICENSE LEVEL and GRIT, atop the card column. */
  level?: ReactNode;
  /** The HASE steppers in place of the HASE cells, and the section's flag and budget. */
  hase?: { body: ReactNode; flag?: SectionFlag; aside: ReactNode };
  bondmate?: ReactNode;
  /** The background's origin picker and custom text, in place of the read-only BACKGROUND. */
  background?: ReactNode;
  /** The loadout groups with EDIT's meters, flags and always-present empty sections. */
  groups?: (loadout: LoadoutGroup[], build: LoadoutGroup[]) => LoadoutGroup[];
  rowActions?: (row: LoadoutRow, group: LoadoutGroup) => ReactNode;
  groupFoot?: (group: LoadoutGroup) => ReactNode;
  rowClass?: (row: LoadoutRow, group: LoadoutGroup) => string | undefined;
  /** NOTES' writer while EDIT is on, for an editing host that is not the owner (the GM). */
  commitNotes?: (value: string) => void;
  overlay?: ReactNode;
};

export function usePilotSheetEdit({
  edit,
  pilot,
  computed,
  compendium,
  onOpenInCompendium,
}: {
  edit: PilotSheetEdit | null;
  pilot: PilotData;
  computed: ComputedPilot | undefined;
  compendium: Compendium;
  onOpenInCompendium: (ref: Ref) => void;
}): PilotSheetEditSlots {
  const [explorer, setExplorer] = useState<Explorer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imgBusy, setImgBusy] = useState(false);
  const source = useMemo(() => new CompendiumSource(compendium), [compendium]);

  // The level-up baseline: captured when EDIT turns on (or the pilot changes under it), dropped when it turns off.
  const on = !!edit?.editing;
  const [baseline, setBaseline] = useState<PilotData | null>(null);
  if (on && baseline?.id !== pilot.id) setBaseline(snapshotBaseline(pilot));
  if (!on && baseline !== null) setBaseline(null);

  const dispatch = (cmd: RosterCommand) => {
    if (!edit) return;
    const res = edit.dispatch(cmd);
    setError(res.ok ? null : res.error);
  };
  const ids = { actorId: edit?.actorId ?? "", pilotId: pilot.id };
  // Draft-buffered like the desktop editor's: one command on debounce or blur.
  const callsignDraft = useDraftText(pilot.callsign, (value) => dispatch({ type: "set_pilot_text", ...ids, field: "callsign", value }));
  const nameDraft = useDraftText(pilot.name, (value) => dispatch({ type: "set_pilot_text", ...ids, field: "name", value }));
  const backgroundDraft = useDraftText(pilot.background ?? "", (name) => dispatch({ type: "set_pilot_background", ...ids, name }));

  if (!edit) return {};
  const toggle = <EditToggle edit={edit} />;
  if (!edit.editing) return { toggle };

  const diff = decorationFor(baseline ? { baseline } : undefined);

  const bySection = new Map<BuildSection, BuildSignal[]>();
  let unspent = 0;
  let invalid = 0;
  const checklist: string[] = [];
  for (const signal of buildSignals({ pilot, computed, compendium })) {
    if (signal.section === "Loadout") continue; // mech-scoped: the MECH sheet owns it
    bySection.set(signal.section, [...(bySection.get(signal.section) ?? []), signal]);
    checklist.push(`${signal.severity === "warn" ? "⚠" : "▲"} ${signal.section} — ${signal.message}`);
    if (signal.severity === "warn") invalid += 1;
    else unspent += 1;
  }

  const haseUsed = pilot.mechSkills.hull + pilot.mechSkills.agi + pilot.mechSkills.sys + pilot.mechSkills.eng;
  const haseMax = haseBudget(pilot);
  const budgets: Record<string, { used: number; max: number; label: string }> = {
    triggers: { used: rankSum(pilot.skills), max: skillBudget(pilot), label: "RANKS" },
    talents: { used: rankSum(pilot.talents), max: talentBudget(pilot), label: "RANKS" },
    licenses: { used: spentLicenseRanks(pilot), max: licenseBudget(pilot), label: "RANKS" },
  };
  const atBudget = (id: string) => budgets[id]!.used >= budgets[id]!.max;
  const coreBonuses = coreBonusAffordance(pilot, compendium);
  const pilotGear = pilotGearAffordance(pilot, compendium);
  const triggerCeiling = rankCeilingAtLevel(pilot.level, MAX_TRIGGER_RANK);

  const addableSkills = sortByName(filterUnowned(compendium.list("skill"), pilot.skills));
  const addableTalents = sortByName(filterUnowned(compendium.list("talent"), pilot.talents));
  const backgrounds = sortByName(compendium.list("background"));
  // One license per frame, by the slug the reducer's equip gate derives; GMS is seeded and never listed.
  const frameLicenses = (() => {
    const seen = new Set<string>();
    const out: { id: string; name: string; source?: string }[] = [];
    for (const f of compendium.list("frame")) {
      const name = f.source === "GMS" ? "GMS" : f.name;
      if (!name) continue;
      const id = licenseSlug(name);
      if (id === GMS_LICENSE_ID || seen.has(id)) continue;
      seen.add(id);
      out.push({ id, name, source: f.source });
    }
    return sortByName(out);
  })();
  const addableLicenses = frameLicenses.filter((l) => licenseRankOf(pilot, l.id) === 0);
  const addableCoreBonuses = sortByName(coreBonuses.rows.filter((r) => r.status.state !== "held" && r.status.state !== "held-invalid"));
  const addableGear = (category: PilotGearCategory) => sortByName(pilotGear.rows.filter((r) => r.category === category && r.status.state !== "held"));

  async function onImage(file: File) {
    const rejected = imageRejection(file);
    if (rejected) return setError(rejected);
    setImgBusy(true);
    const uploaded = await uploadPilotImage(pilot.id, file);
    if (uploaded.ok) dispatch({ type: "set_pilot_text", ...ids, field: "image", value: uploaded.value });
    else setError(uploaded.error);
    setImgBusy(false);
  }

  const nameEditor = (
    <div className="unit-sheet-name-edit" data-build-anchor="callsign">
      <input
        className="unit-sheet-name-input"
        value={callsignDraft.value}
        placeholder="CALLSIGN"
        aria-label="Callsign"
        onFocus={callsignDraft.onFocus}
        onChange={(e) => callsignDraft.onChange(e.target.value)}
        onBlur={callsignDraft.onBlur}
      />
      <input
        className="unit-sheet-name-input is-small"
        value={nameDraft.value}
        placeholder="Full name, on Union papers"
        aria-label="Name"
        onFocus={nameDraft.onFocus}
        onChange={(e) => nameDraft.onChange(e.target.value)}
        onBlur={nameDraft.onBlur}
      />
    </div>
  );

  const headerControls = (
    <>
      <ImageUpload label="Upload pilot profile image" busy={imgBusy} hasImage={!!pilot.image} noun="PORTRAIT" onFile={onImage} />
      <span className="unit-sheet-field-label">PNG · JPG · WEBP · 5MB MAX</span>
    </>
  );

  const toolbar = (
    <EditBar edit={edit}>
      <div className="unit-sheet-buildbar" title={checklist.join("\n") || undefined}>
        <span className="unit-sheet-field-label">BUILD</span>
        {unspent === 0 && invalid === 0 ? (
          <span className="unit-sheet-buildchip is-ok">BUILD COMPLETE</span>
        ) : (
          <>
            {unspent > 0 && <span className="unit-sheet-buildchip">{unspent} UNSPENT</span>}
            {invalid > 0 && <span className="unit-sheet-buildchip is-warn">{invalid} INVALID</span>}
          </>
        )}
      </div>
    </EditBar>
  );

  const grit = computed?.grit ?? Math.ceil(pilot.level / 2);
  const level = (
    <div className="unit-sheet-level-edit">
      <div>
        <div className="unit-sheet-field-label">LICENSE LEVEL · 0–12</div>
        <Stepper
          display={String(pilot.level).padStart(2, "0")}
          label="License Level"
          canDec={pilot.level > 0}
          canInc={pilot.level < 12}
          onDec={() => dispatch({ type: "set_level", ...ids, level: pilot.level - 1 })}
          onInc={() => dispatch({ type: "set_level", ...ids, level: pilot.level + 1 })}
        />
      </div>
      <div>
        <div className="unit-sheet-field-label">GRIT · ⌈LL÷2⌉</div>
        <b>+{grit}</b>
      </div>
    </div>
  );

  const hase = {
    flag: sectionFlag(bySection.get("Mech Skills")),
    aside: (
      <span className={`unit-sheet-meter${haseUsed > haseMax ? " is-over" : ""}`}>
        <span className="unit-sheet-meter-num">
          POINTS {haseUsed} <small>/ {haseMax}</small>
        </span>
      </span>
    ),
    body: (
      <div className="unit-sheet-hase-edit">
        {HASE.map(({ key, label, abbr }) => {
          const value = pilot.mechSkills[key];
          return (
            <div key={key} className={`unit-sheet-hase-cell${diff?.haseGained(key, value) ? " is-gained" : ""}`}>
              <span className="unit-sheet-label">{abbr}</span>
              <Stepper
                display={String(value)}
                label={label}
                canDec={value > 0}
                canInc={value < 6 && haseUsed < haseMax}
                onDec={() => dispatch({ type: "set_mech_skill", ...ids, skill: key, value: value - 1 })}
                onInc={() => dispatch({ type: "set_mech_skill", ...ids, skill: key, value: value + 1 })}
              />
            </div>
          );
        })}
      </div>
    ),
  };

  // Bonded (core p.90) names another pilot; the field shows once the talent is held, or while a stale pick remains.
  const bondmate =
    (pilot.talents["t_bonded"] ?? 0) > 0 || pilot.bondmate !== undefined ? (
      <select
        className="unit-sheet-field"
        aria-label="Bondmate"
        value={pilot.bondmate ?? ""}
        onChange={(e) => dispatch({ type: "set_pilot_bondmate", ...ids, ...(e.target.value !== "" ? { bondmate: e.target.value } : {}) })}
      >
        <option value="">— none —</option>
        {edit.otherPilots.map((p) => (
          <option key={p.id} value={p.id}>
            {p.callsign}
          </option>
        ))}
      </select>
    ) : undefined;

  const selectedBackground = pilot.backgroundRef
    ? compendium.packs.find((p) => p.id === pilot.backgroundRef!.packId)?.items.background.find((b) => b.id === pilot.backgroundRef!.id)
    : undefined;
  const rollOrigin = () => {
    const b = backgrounds[Math.floor(Math.random() * backgrounds.length)];
    const owned = b ? compendium.getOwned("background", b.id) : undefined;
    if (b && owned) dispatch({ type: "set_pilot_background", ...ids, name: b.name, ref: { id: b.id, packId: owned.packId } });
  };
  const background = pilot.backgroundRef ? (
    <>
      <div className="unit-sheet-bg-name">{pilot.background || selectedBackground?.name || "UNKNOWN BACKGROUND"}</div>
      {selectedBackground ? (
        selectedBackground.description && <RulesText html={selectedBackground.description} className="unit-sheet-bg-prose" />
      ) : (
        <div className="unit-sheet-section-empty">SOURCE PACK {pilot.backgroundRef.packId} IS NOT LOADED · NAME RETAINED</div>
      )}
      <div className="unit-sheet-btnrow">
        <button type="button" className="unit-sheet-mini" onClick={() => setExplorer("background")}>
          CHANGE ORIGIN
        </button>
        <button type="button" className="unit-sheet-mini" onClick={() => dispatch({ type: "set_pilot_background", ...ids, name: pilot.background ?? "" })}>
          MAKE CUSTOM
        </button>
      </div>
    </>
  ) : (
    <>
      <textarea
        className="be-textarea"
        rows={3}
        value={backgroundDraft.value}
        aria-label="Custom background"
        placeholder="Choose an origin, or write your own."
        onFocus={backgroundDraft.onFocus}
        onChange={(e) => backgroundDraft.onChange(e.target.value)}
        onBlur={backgroundDraft.onBlur}
      />
      <div className="unit-sheet-btnrow">
        <button type="button" className="unit-sheet-mini" disabled={backgrounds.length === 0} onClick={() => setExplorer("background")}>
          CHOOSE ORIGIN
        </button>
        <button type="button" className="unit-sheet-mini" disabled={backgrounds.length === 0} onClick={rollOrigin}>
          ROLL ORIGIN
        </button>
      </div>
    </>
  );

  const groups = (loadout: LoadoutGroup[], build: LoadoutGroup[]): LoadoutGroup[] => {
    // Every gear category shows, held or not, so its ＋ ADD has a home.
    const gear = (Object.keys(GEAR_GROUP) as PilotGearCategory[]).map((category) => {
      const { id, title } = GEAR_GROUP[category];
      const tally = pilotGear.categories.find((c) => c.category === category)!;
      const group = loadout.find((g) => g.id === id) ?? { id, title, rows: [], empty: "— none carried —" };
      return { ...group, meter: { label: "CARRIED", used: tally.used, max: tally.limit } };
    });
    const withBudgets = build.map((g): LoadoutGroup => {
      if (g.id === "core-bonuses") {
        return { ...g, meter: { label: "TAKEN", used: coreBonuses.used, max: coreBonuses.budget }, ...(coreBonuses.budget === 0 ? { empty: "— earned at every 3rd LL (3/6/9/12) —" } : {}) };
      }
      const budget = budgets[g.id];
      return budget ? { ...g, meter: budget } : g;
    });
    return [...gear, ...withBudgets].map((g) => {
      const section = GROUP_SECTION[g.id];
      if (section === undefined) return g;
      const flag = sectionFlag(bySection.get(section));
      return { ...g, anchor: SECTION_ANCHOR[section], ...(flag ? { flag } : {}) };
    });
  };

  const remove = (name: string, cmd: RosterCommand) => (
    <button type="button" className="unit-sheet-mini" aria-label={`remove ${name}`} onClick={() => dispatch(cmd)}>
      <Icon icon={faXmark} />
    </button>
  );

  const rowActions = (row: LoadoutRow, group: LoadoutGroup): ReactNode => {
    const id = row.ref?.id;
    if (id === undefined) return undefined;
    switch (group.id) {
      case "triggers": {
        const rank = pilot.skills[id] ?? 0;
        return (
          <>
            <Stepper
              display={`+${triggerBonus(rank)}`}
              label={row.name}
              canDec={rank > 1}
              canInc={rank < triggerCeiling && !atBudget("triggers")}
              onDec={() => dispatch({ type: "set_skill_trigger", ...ids, skillId: id, rank: rank - 1 })}
              onInc={() => dispatch({ type: "set_skill_trigger", ...ids, skillId: id, rank: rank + 1 })}
            />
            {remove(row.name, { type: "set_skill_trigger", ...ids, skillId: id, rank: 0 })}
          </>
        );
      }
      case "talents": {
        const rank = pilot.talents[id] ?? 0;
        const maxRank = compendium.getById("talent", id)?.ranks?.length || 3;
        return (
          <>
            <Stepper
              display={ROMAN[rank] ?? String(rank)}
              label={row.name}
              accent
              canDec={rank > 1}
              canInc={rank < rankCeilingAtLevel(pilot.level, maxRank) && !atBudget("talents")}
              onDec={() => dispatch({ type: "set_talent_rank", ...ids, talentId: id, rank: rank - 1 })}
              onInc={() => dispatch({ type: "set_talent_rank", ...ids, talentId: id, rank: rank + 1 })}
            />
            {remove(row.name, { type: "set_talent_rank", ...ids, talentId: id, rank: 0 })}
          </>
        );
      }
      case "licenses": {
        const rank = licenseRankOf(pilot, id);
        return (
          <>
            <Stepper
              display={ROMAN[rank] ?? String(rank)}
              label={row.name}
              accent
              canDec={rank > 0}
              canInc={rank < MAX_LICENSE_RANK && !atBudget("licenses")}
              onDec={() => dispatch({ type: "set_license_rank", ...ids, licenseId: id, rank: rank - 1 })}
              onInc={() => dispatch({ type: "set_license_rank", ...ids, licenseId: id, rank: rank + 1 })}
            />
            {remove(row.name, { type: "set_license_rank", ...ids, licenseId: id, rank: 0 })}
          </>
        );
      }
      case "core-bonuses": {
        const status = coreBonuses.rows.find((r) => r.id === id)?.status;
        return (
          <>
            {status?.state === "held-invalid" && (
              <span className="unit-sheet-mini is-reserved" title={"reason" in status ? status.reason : undefined}>
                <Icon icon={faTriangleExclamation} className="icon-lead" />
                UNDER-RANKED
              </span>
            )}
            {remove(row.name, { type: "set_core_bonus", ...ids, bonusId: id, taken: false })}
          </>
        );
      }
      case "weapons":
      case "armor":
      case "gear": {
        // The item's own stat grant (the COMPENDIUM card's formatter): `=N` is a SET, `+N` an ADD.
        const item = compendium.getById("pilot_gear", id) as PilotGear | undefined;
        // Set Damage Type (the Signatures): the damage type every attack uses; unpicked, it is asked per attack.
        const setsDamageType = group.id === "weapons" && (item?.tags ?? []).some((t) => t.id === SET_DAMAGE_TYPE_TAG);
        const picked = pilot.gearDamageTypes?.[id];
        // The caption names the picked type in place of the printed VARIABLE, as the row's damage does.
        const profile = (item ? pilotGearProfile(item) : []).map((p) => (picked !== undefined && p.k === "VARIABLE" ? { ...p, k: picked.toUpperCase() } : p));
        return (
          <>
            {profile.length > 0 && <span className="unit-sheet-gear-grants">GRANTS {profile.map((p) => `${p.k} ${p.v}`).join(" · ")}</span>}
            {setsDamageType && (
              <select
                className="unit-sheet-mini unit-sheet-damage-pick"
                aria-label={`${row.name} damage type`}
                value={picked ?? ""}
                onChange={(e) => {
                  const damageType = CHOSEN_DAMAGE_TYPES.find((d) => d === e.target.value);
                  dispatch({ type: "set_pilot_gear_damage_type", ...ids, gearId: id, ...(damageType ? { damageType } : {}) });
                }}
              >
                <option value="">DAMAGE · PER ATTACK</option>
                {CHOSEN_DAMAGE_TYPES.map((d) => (
                  <option key={d} value={d}>
                    {d.toUpperCase()}
                  </option>
                ))}
              </select>
            )}
            {remove(row.name, { type: "remove_pilot_gear", ...ids, gearId: id })}
          </>
        );
      }
    }
    return undefined;
  };

  const add = ({ label, aria, disabled, open }: { label: string; aria: string; disabled: boolean; open: Explorer }) => (
    <div className="unit-sheet-lgroup-foot">
      <button type="button" className="unit-sheet-mini" aria-label={aria} disabled={disabled} data-forge-focus="" onClick={() => setExplorer(open)}>
        <Icon icon={faPlus} className="icon-lead" />
        {label}
      </button>
    </div>
  );
  const groupFoot = (group: LoadoutGroup): ReactNode => {
    switch (group.id) {
      case "triggers":
        return add({ label: "ADD TRIGGER", aria: "Add skill trigger", disabled: atBudget("triggers") || addableSkills.length === 0, open: "skill" });
      case "talents":
        return add({ label: "ADD TALENT", aria: "Add talent", disabled: atBudget("talents") || addableTalents.length === 0, open: "talent" });
      case "licenses":
        return add({ label: "ADD LICENSE", aria: "Add license", disabled: atBudget("licenses") || addableLicenses.length === 0, open: "license" });
      case "core-bonuses":
        return add({ label: "ADD CORE BONUS", aria: "Add core bonus", disabled: coreBonuses.atBudget || addableCoreBonuses.length === 0, open: "core_bonus" });
    }
    const category = (Object.keys(GEAR_GROUP) as PilotGearCategory[]).find((c) => GEAR_GROUP[c].id === group.id);
    if (!category) return undefined;
    const label = pilotGearCategoryLabel(category);
    const tally = pilotGear.categories.find((c) => c.category === category)!;
    return add({ label: `ADD ${label.toUpperCase()}`, aria: `Add ${label.toLowerCase()}`, disabled: tally.used >= tally.limit || addableGear(category).length === 0, open: `gear:${category}` });
  };

  const rowClass = (row: LoadoutRow, group: LoadoutGroup): string | undefined => {
    const id = row.ref?.id;
    if (!diff || id === undefined) return undefined;
    const gained =
      (group.id === "triggers" && diff.skillGained(id, pilot.skills[id] ?? 0)) ||
      (group.id === "talents" && diff.talentGained(id, pilot.talents[id] ?? 0)) ||
      (group.id === "licenses" && diff.licenseGained(id, licenseRankOf(pilot, id)));
    return gained ? "is-gained" : undefined;
  };

  const reader = (type: Ref["type"]) => (id: string) => <CompendiumRecordReader source={source} item={{ type, id }} onNavigate={onOpenInCompendium} />;
  const close = () => setExplorer(null);
  const pick = (cmd: RosterCommand) => {
    dispatch(cmd);
    close();
  };
  let overlay: ReactNode;
  if (explorer === "background") {
    overlay = (
      <CompendiumExplorer
        title="CHOOSE PILOT ORIGIN"
        entries={backgrounds.map((b) => ({ id: b.id, name: b.name, meta: "PILOT ORIGIN", terse: b.description }))}
        renderDetail={reader("background")}
        onSelect={(id) => {
          const b = compendium.getById("background", id);
          const owned = compendium.getOwned("background", id);
          if (b && owned) pick({ type: "set_pilot_background", ...ids, name: b.name, ref: { id, packId: owned.packId } });
        }}
        onClose={close}
      />
    );
  } else if (explorer === "skill") {
    overlay = (
      <CompendiumExplorer
        title="ADD TRIGGER"
        entries={addableSkills.map((s) => ({ id: s.id, name: s.name ?? s.id }))}
        renderDetail={reader("skill")}
        onSelect={(id) => pick({ type: "set_skill_trigger", ...ids, skillId: id, rank: 1 })}
        onClose={close}
      />
    );
  } else if (explorer === "talent") {
    overlay = (
      <CompendiumExplorer
        title="ADD TALENT"
        accent
        entries={addableTalents.map((t) => ({ id: t.id, name: t.name ?? t.id, meta: "TALENT" }))}
        renderDetail={reader("talent")}
        onSelect={(id) => pick({ type: "set_talent_rank", ...ids, talentId: id, rank: 1 })}
        onClose={close}
      />
    );
  } else if (explorer === "license") {
    overlay = (
      <CompendiumExplorer
        title="ADD LICENSE"
        accent
        entries={addableLicenses.map((l) => ({ id: l.id, name: l.name, meta: l.source?.toUpperCase() }))}
        renderDetail={reader("license")}
        onSelect={(id) => pick({ type: "set_license_rank", ...ids, licenseId: id, rank: 1 })}
        onClose={close}
      />
    );
  } else if (explorer === "core_bonus") {
    overlay = (
      <CompendiumExplorer
        title="ADD CORE BONUS"
        accent
        entries={addableCoreBonuses.map((r) => ({
          id: r.id,
          name: r.name,
          meta: [r.source?.toUpperCase(), "reason" in r.status ? r.status.reason : undefined].filter(Boolean).join(" · ") || undefined,
        }))}
        renderDetail={reader("core_bonus")}
        onSelect={(id) => pick({ type: "set_core_bonus", ...ids, bonusId: id, taken: true })}
        onClose={close}
      />
    );
  } else if (explorer?.startsWith("gear:")) {
    const category = explorer.slice("gear:".length) as PilotGearCategory;
    const label = pilotGearCategoryLabel(category).toUpperCase();
    overlay = (
      <CompendiumExplorer
        title={`ADD ${label}`}
        entries={addableGear(category).map((r) => ({ id: r.id, name: r.name, meta: label }))}
        renderDetail={reader("pilot_gear")}
        onSelect={(id) => pick({ type: "add_pilot_gear", ...ids, gearId: id })}
        onClose={close}
      />
    );
  }

  return {
    toggle,
    nameEditor,
    headerControls,
    toolbar,
    error,
    level,
    hase,
    bondmate,
    background,
    groups,
    rowActions,
    groupFoot,
    rowClass,
    commitNotes: (value) => dispatch({ type: "set_pilot_text", ...ids, field: "notes", value }),
    overlay,
  };
}
